import React from "react";
import { Col } from "react-bootstrap";
import { FormikProps } from "formik";

import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { FormatHelper } from "../../../assets/utils/helpers";
import {
  SearchDto,
  SearchFilter,
  SearchOperator,
  SortDirection,
  SortModel,
} from "../../../assets/utils/lib/searchDto";
import Control from "../../../assets/vfx/controls/control";

import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { GRID_ACTION_TYPES } from "../../../assets/types/common";
import { Constants } from "../../../assets/utils/lib/constants";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import Form from "../../../assets/vfx/form/form";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { FormViewDto } from "../../../services/dto";
import { QuestionnaireType } from "./questionnaireContainer";
import { QuestionnaireFormLookupProps } from "./questionnaireForm";
import { QuestionnaireService } from "./questionnaireService";
import { useCommunity } from "../../../context/communityContext";

const sortBy: SortModel[] = [
    { field: "activeFromDate", direction: SortDirection.Descending },
    { field: "activeToDate", direction: SortDirection.Descending },
    { field: "name", direction: SortDirection.Ascending },
];

const defaultSearchQuery: SearchDto = {
  filters: [{ field: "isDeleted", value: false }],
  pageSize: 10,
  pageNumber: 1,
  sorts: sortBy,
};

interface QuestionnaireFormSearch {
  communityId: string;
  typeId: string;
  tagId: string;
  questionTypeId: string;
}

interface QuestionnaireFormPageProps {
  lookups: QuestionnaireFormLookupProps | undefined;
  controlStyles?: IControlStyles | undefined;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  questionnaireType?: string;
}
const DATA_ID_FIELD = "id";

const renderDateRangeCell = (
  _title: string,
  rowData: FormViewDto
): React.JSX.Element | null => {
  const startDate = rowData?.activeFromDate
    ? FormatHelper.formatDate(rowData.activeFromDate)
    : null;
  const endDate = rowData?.activeToDate
    ? FormatHelper.formatDate(rowData.activeToDate)
    : null;

  if (!startDate && !endDate) return null;

  if (startDate && endDate) {
    return (
      <span
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          columnGap: 8,
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        <span style={{ textAlign: "left" }}>{startDate}</span>
        <span style={{ textAlign: "center" }}>-</span>
        <span style={{ textAlign: "right" }}>{endDate}</span>
      </span>
    );
  }

  return <span style={{ whiteSpace: "nowrap" }}>{startDate ?? `End: ${endDate}`}</span>;
};

const createGridColumns = (
  isPollView?: boolean,
  isFeedbackView?: boolean,
  isSurveyView?: boolean
): ControlDataGridColumnProps[] => [
  {
    data: "id",
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "typeName",
    title: "Type",
    visible: !isPollView && !isFeedbackView && !isSurveyView,
    columnType: "text",
    alignment: "left",
    sortable: false,
    width: "24%",
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "15%",
  },
  {
    data: "communityName",
    title: "Community",
    visible: !isPollView && !isFeedbackView && !isSurveyView,
    columnType: "text",
    alignment: "left",
    width: "20%",
  },
  {
    data: "question",
    title: "Question",
    visible: isPollView || isFeedbackView,
    columnType: "text",
    alignment: "left",
  },
  {
    data: "questionType",
    title: "Question Type",
    visible: isPollView,
    columnType: "text",
    alignment: "left",
    width: "15%",
  },
  {
    data: "activeFromDate",
    title: "Dates",
    visible: true,
    columnType: "custom",
    alignment: "left",
    width: "15%",
    customCell: renderDateRangeCell,
  },
];

const sameFilters = (a: SearchFilter[], b: SearchFilter[]) =>
  a.length === b.length &&
  a.every(
    (f, i) =>
      f.field === b[i].field &&
      f.operator === b[i].operator &&
      String(f.value) === String(b[i].value)
  );

function upsertFilter(
  list: SearchFilter[],
  filter: SearchFilter
): SearchFilter[] {
  const next = list.filter((x) => x.field !== filter.field);
  next.push(filter);
  return next;
}

const getFilterValue = (filters: SearchFilter[], field: string): string =>
  String(filters.find((x) => x.field === field)?.value ?? Constants.emptyString);

const hasSelectedValue = (value?: string): boolean =>
  !!value && value !== Constants.emptyString && value !== Constants.emptyGuid;

const getQuestionnaireTypeLabel = (id?: string): string => {
    if (id === QuestionnaireType.Poll) return "Poll";
    if (id === QuestionnaireType.Feedback) return "FeedBack";
    if (id === QuestionnaireType.Survey) return "Survey";
    return "Questionnaire";
};

export const QuestionnairePage = (props: QuestionnaireFormPageProps) => {
  const title: string = getQuestionnaireTypeLabel(props.questionnaireType);
  const styles: IControlStyles = props?.controlStyles ?? useControlStyles();
  const { communityId } = useCommunity();
  const hasContextCommunity =
    !!communityId &&
    communityId !== Constants.emptyGuid &&
    communityId !== Constants.emptyString;
  const isEmptyOrg = !hasContextCommunity;
  const baseFilters: SearchFilter[] = React.useMemo(() => {
    const f = [...defaultSearchQuery.filters];
    if (props.questionnaireType) {
      f.push({
        field: "typeId",
        operator: SearchOperator.Equal,
        value: props.questionnaireType,
      });
    }
    return f;
  }, [props.questionnaireType]);
  const scopedBaseFilters: SearchFilter[] = React.useMemo(() => {
    if (!hasContextCommunity) return baseFilters;

    return upsertFilter(baseFilters, {
      field: "communityId",
      operator: SearchOperator.Contains,
      value: communityId,
    });
  }, [baseFilters, communityId, hasContextCommunity]);
  const [searchQuery, setSearchQuery] = React.useState<SearchDto>({
    ...defaultSearchQuery,
    filters: scopedBaseFilters,
  });

  React.useEffect(() => {
    setSearchQuery((prev) => {
      const nextFilters = [...scopedBaseFilters];

      if (sameFilters(prev.filters, nextFilters)) return prev;

      return {
        ...prev,
        pageNumber: 1,
        filters: nextFilters,
      };
    });
  }, [scopedBaseFilters]);

  const questionnaireGridQuery = QuestionnaireService.gridQuery(searchQuery);
  const { data } = questionnaireGridQuery;
  const isGridLoading =
    questionnaireGridQuery.isLoading || questionnaireGridQuery.isFetching;
  const questionnaireForms: FormViewDto[] = data?.result ?? [];
  const hasData = questionnaireForms.length > 0;
  const isPollView = props.questionnaireType === QuestionnaireType.Poll;
  const isFeedBackView = props.questionnaireType === QuestionnaireType.Feedback;
  const isSurveyView = props.questionnaireType === QuestionnaireType.Survey;
  const gridColumns = createGridColumns(isPollView, isFeedBackView, isSurveyView);
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
    GRID_ACTION_TYPES.DELETE
  ]);
  const layoutOptions = createLayoutOptions(hasData);
  const questionTypeOptions = isPollView
    ? props.lookups?.pollQuestionTypes ?? props.lookups?.questionTypes
    : isSurveyView
      ? props.lookups?.surveyQuestionTypes ?? props.lookups?.questionTypes
      : props.lookups?.questionTypes;

  const getUpdatedSearchQuery = (values: QuestionnaireFormSearch): SearchDto => {
    let filters = [...scopedBaseFilters];

    if (hasSelectedValue(values.communityId)) {
      filters = upsertFilter(filters, {
        field: "communityId",
        operator: SearchOperator.Contains,
        value: values.communityId,
      });
    }
    if (hasSelectedValue(values.typeId)) {
      filters = upsertFilter(filters, {
        field: "typeId",
        operator: SearchOperator.Contains,
        value: values.typeId,
      });
    }
    if (hasSelectedValue(values.tagId)) {
      filters = upsertFilter(filters, {
        field: "tagId",
        operator: SearchOperator.Contains,
        value: values.tagId,
      });
    }
    if (hasSelectedValue(values.questionTypeId)) {
      filters = upsertFilter(filters, {
        field: "questionTypeId",
        operator: SearchOperator.Contains,
        value: values.questionTypeId,
      });
    }

    return {
      ...defaultSearchQuery,
      filters,
    };
  };

  const handleSearch = (values: QuestionnaireFormSearch) => {
    setSearchQuery(getUpdatedSearchQuery(values));
  };

  const handleDropdownChange = (
    formikProps: FormikProps<object>,
    field: keyof QuestionnaireFormSearch,
    value: string
  ) => {
    setSearchQuery(
      getUpdatedSearchQuery({
        ...(formikProps.values as QuestionnaireFormSearch),
        [field]: value,
      })
    );
  };

  function handleNew() {
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);
  }

  const handleClear = () => {
    setSearchQuery({
      ...defaultSearchQuery,
      filters: scopedBaseFilters,
    });
  };

  const searchContainer: React.JSX.Element = (
    <SearchContainer
      initialValues={{
        communityId:
          getFilterValue(searchQuery.filters, "communityId") ?? Constants.emptyString,
        typeId: !isPollView && !isFeedBackView && !isSurveyView
          ? getFilterValue(searchQuery.filters, "typeId")
          : Constants.emptyString,
        tagId: getFilterValue(searchQuery.filters, "tagId"),
        questionTypeId: isPollView
          ? getFilterValue(searchQuery.filters, "questionTypeId")
          : Constants.emptyString,
      }}
      onSearch={handleSearch}
      onClear={handleClear}
      enableReinitialize
    >
      {(formikProps: FormikProps<object>) => (
        <>
          {!isEmptyOrg ? null : (
            <Col lg={3}>
              <Form.Dropdown
                name={"communityId"}
                label={"Community"}
                options={props?.lookups?.community}
                blankOption={{
                  key: Constants.emptyGuid,
                  display: "Select Community",
                  value: Constants.emptyGuid,
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDropdownChange(
                    formikProps,
                    "communityId",
                    event.target.value
                  )
                }
              />
            </Col>
          )}
          {!isPollView && !isFeedBackView && !isSurveyView && (
            <Col lg={3} className={isEmptyOrg ? "ms-2" : "ms-0"}>
              <Form.Dropdown
                name={"typeId"}
                label={"Type"}
                options={props?.lookups?.questionnaireLookupValueType}
                blankOption={{
                  key: Constants.emptyGuid,
                  display: "Select Type",
                  value: Constants.emptyGuid,
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDropdownChange(
                    formikProps,
                    "typeId",
                    event.target.value
                  )
                }
              />
            </Col>
          )}
          {!isPollView && !isFeedBackView && !isSurveyView && (
            <Col
              lg={3}
              className={isEmptyOrg ? "ms-2" : "ms-0"}
            >
              <Form.Dropdown
                name={"tagId"}
                label={"Tag"}
                options={props?.lookups?.tags}
                blankOption={{
                  key: Constants.emptyGuid,
                  display: "Select Tag",
                  value: Constants.emptyGuid,
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDropdownChange(
                    formikProps,
                    "tagId",
                    event.target.value
                  )
                }
              />
            </Col>
          )}
          {isPollView && (
            <Col lg={3} className="ms-2">
              <Form.Dropdown
                name={"questionTypeId"}
                label={"Question Type"}
                options={questionTypeOptions}
                blankOption={{
                  key: Constants.emptyGuid,
                  display: "Select Question Type",
                  value: Constants.emptyGuid,
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDropdownChange(
                    formikProps,
                    "questionTypeId",
                    event.target.value
                  )
                }
              />
            </Col>
          )}
        </>
      )}
    </SearchContainer>
  );

  return (
      <PageContent hideHeader>
        <Control.DataGrid
          dataId={DATA_ID_FIELD}
          cols={gridColumns}
          data={questionnaireForms}
          actionCol={{...actionColumn, width: "170px"}}
          layoutOptions={layoutOptions}
          onCustomEvent={props?.onCustomEvent}
          totalRecords={data?.totalSize ?? data?.result?.length ?? 0}
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) =>
            setSearchQuery((query) => ({
              ...query,
              ...patch,
              sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
            }))
          }
          uiOptions={{
            title,
            filterContent: searchContainer,
            primaryAction: {
              text: `Add ${title}`,
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}
        />
    </PageContent>
  );
};
